import type { LensCard, LensCardData } from '~/types/leLens';

export type LensVisualIssueKind = 'chart-fit' | 'data' | 'overflow' | 'overlap';

export type LensVisualIssue = {
  cardId: string;
  cardTitle: string;
  kind: LensVisualIssueKind;
  message: string;
};

const CHART_TYPES = new Set<LensCard['type']>([
  'area',
  'bar',
  'boxplot',
  'combo',
  'drivers',
  'heatmap',
  'line',
  'metric',
  'pie',
  'scatter',
  'stacked',
]);

function cardIssue(card: LensCard, kind: LensVisualIssueKind, message: string): LensVisualIssue {
  return { cardId: card.id, cardTitle: card.title, kind, message };
}

function populatedValues(rows: Array<Record<string, unknown>>): Map<string, Array<unknown>> {
  const values = new Map<string, Array<unknown>>();
  rows.forEach((row) => {
    Object.entries(row).forEach(([column, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        const columnValues = values.get(column);
        if (columnValues) {
          columnValues.push(value);
        } else {
          values.set(column, [value]);
        }
      }
    });
  });
  return values;
}

function isNumber(value: unknown): boolean {
  return typeof value === 'number' && Number.isFinite(value);
}

export function chartFitIssue(card: LensCard, rows: Array<Record<string, unknown>>): string | undefined {
  if (!CHART_TYPES.has(card.type)) return undefined;

  const values = populatedValues(rows);
  const numericColumns = [...values.values()].filter((column) => column.some(isNumber)).length;
  const categoryColumns = [...values.values()].filter(
    (column) => column.some((value) => !isNumber(value)) && new Set(column.map(String)).size > 1,
  );

  if (card.type === 'metric' && numericColumns === 0) return 'The metric has no numeric value to display.';
  if ((card.type === 'line' || card.type === 'area') && (rows.length < 2 || numericColumns === 0)) {
    return 'This trend chart needs at least two points and a numeric measure.';
  }
  if (['bar', 'drivers', 'stacked'].includes(card.type) && (numericColumns === 0 || categoryColumns.length === 0)) {
    return 'This comparison chart needs both a category and a numeric measure.';
  }
  if (card.type === 'pie') {
    if (numericColumns === 0 || categoryColumns.length === 0) return 'This pie needs a category and a numeric share.';
    if (Math.max(...categoryColumns.map((column) => new Set(column.map(String)).size)) > 8) {
      return 'This pie has too many categories to read clearly; a bar chart would be more suitable.';
    }
  }
  if (card.type === 'scatter' && (rows.length < 3 || numericColumns < 2)) {
    return 'This scatter plot needs at least three observations and two numeric measures.';
  }
  if (card.type === 'combo' && numericColumns < 2) return 'This combo chart needs at least two numeric measures.';
  if (card.type === 'heatmap' && (categoryColumns.length < 2 || numericColumns === 0)) {
    return 'This heatmap needs two categorical dimensions and a numeric measure.';
  }
  if (card.type === 'boxplot' && (rows.length < 5 || numericColumns === 0)) {
    return 'This box plot needs enough numeric observations to show a distribution.';
  }
  return undefined;
}

/** Checks the result each card actually received, including whether its chosen visual can use it. */
export function auditLensCardResults(
  cards: ReadonlyArray<LensCard>,
  dataByCardId: ReadonlyMap<string, LensCardData>,
): Array<LensVisualIssue> {
  const issues: Array<LensVisualIssue> = [];
  cards.forEach((card) => {
    if (card.type === 'insight') return;
    const data = dataByCardId.get(card.id);
    if (!data) {
      issues.push(cardIssue(card, 'data', 'No result was returned for this card.'));
      return;
    }
    if (data.status !== 'ready') {
      const message =
        data.status === 'empty'
          ? 'The query returned no rows for this period.'
          : data.message || `The card result is ${data.status}.`;
      issues.push(cardIssue(card, 'data', message));
      return;
    }
    const rows = data.rows ?? [];
    if (rows.length === 0) {
      issues.push(cardIssue(card, 'data', 'The query returned no rows for this period.'));
      return;
    }
    const fit = chartFitIssue(card, rows);
    if (fit) issues.push(cardIssue(card, 'chart-fit', fit));
  });
  return issues;
}

function overlapArea(left: DOMRect, right: DOMRect): number {
  const width = Math.max(0, Math.min(left.right, right.right) - Math.max(left.left, right.left));
  const height = Math.max(0, Math.min(left.bottom, right.bottom) - Math.max(left.top, right.top));
  return width * height;
}

/** Checks the frames the browser painted, rather than assuming saved grid coordinates rendered safely. */
export function auditLensCardElements(root: HTMLElement, cards: ReadonlyArray<LensCard>): Array<LensVisualIssue> {
  const byId = new Map(cards.map((card) => [card.id, card]));
  const frames = [...root.querySelectorAll<HTMLElement>('[data-lens-card-frame]')];
  const issues: Array<LensVisualIssue> = [];

  frames.forEach((frame) => {
    const card = byId.get(frame.dataset.lensCardFrame ?? '');
    const content = frame.querySelector<HTMLElement>('[data-lens-card-content]');
    if (!card || !content) return;
    // A reading sidecar intentionally occupies free canvas columns beside the saved card frame.
    // Its absolute position extends content.scrollWidth, so audit the primary card surface
    // separately when that companion exists. Real clipping inside the card still counts.
    const measured = content.querySelector('[data-lens-reading-sidecar]')
      ? (content.querySelector<HTMLElement>('[data-lens-card-primary="true"]') ?? content)
      : content;
    if (measured.scrollWidth > measured.clientWidth + 2 || measured.scrollHeight > measured.clientHeight + 2) {
      issues.push(cardIssue(card, 'overflow', 'Content is clipped or overflowing its card frame.'));
    }
  });

  for (let leftIndex = 0; leftIndex < frames.length; leftIndex += 1) {
    for (let rightIndex = leftIndex + 1; rightIndex < frames.length; rightIndex += 1) {
      const left = frames[leftIndex];
      const right = frames[rightIndex];
      // Outlines can extend a pixel beyond a frame. Treat only a material painted collision as overlap.
      if (overlapArea(left.getBoundingClientRect(), right.getBoundingClientRect()) <= 4) continue;
      const leftCard = byId.get(left.dataset.lensCardFrame ?? '');
      const rightCard = byId.get(right.dataset.lensCardFrame ?? '');
      if (leftCard)
        issues.push(cardIssue(leftCard, 'overlap', `This card overlaps “${rightCard?.title ?? 'another card'}”.`));
    }
  }

  return issues;
}

function visualIssueKey(issue: LensVisualIssue): string {
  return `${issue.cardId}:${issue.kind}:${issue.message}`;
}

export function uniqueVisualIssues(issues: ReadonlyArray<LensVisualIssue>): Array<LensVisualIssue> {
  return [...new Map(issues.map((issue) => [visualIssueKey(issue), issue])).values()];
}

export function sameVisualIssues(left: ReadonlyArray<LensVisualIssue>, right: ReadonlyArray<LensVisualIssue>): boolean {
  return (
    left.length === right.length && left.every((issue, index) => visualIssueKey(issue) === visualIssueKey(right[index]))
  );
}

/** Only a missing result or execution error can plausibly improve when immediately retried.
 * Empty, restricted and unconfigured results are answers, not transient transport failures. */
export function retryableVisualIssueCardIds(
  issues: ReadonlyArray<LensVisualIssue>,
  dataByCardId: ReadonlyMap<string, LensCardData>,
): Array<string> {
  return [
    ...new Set(
      issues
        .filter((issue) => issue.kind === 'data')
        .filter((issue) => {
          const data = dataByCardId.get(issue.cardId);
          return !data || data.status === 'error';
        })
        .map((issue) => issue.cardId),
    ),
  ];
}
